import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, Utensils, Apple, Pill } from "lucide-react";
import api from "../lib/api";
import { useAutoSync } from "../hooks/useAutoSync";
import { money } from "../lib/format";
import { MEAL_SLOTS, UNITS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const todayStr = () => new Date().toISOString().slice(0, 10);
const shift = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const pretty = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

function AddEntryDialog({ open, onOpenChange, slot, date, foods, meals, supplements, onSaved }) {
  const [type, setType] = useState("meal");
  const [refId, setRefId] = useState("");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("oz");

  useEffect(() => {
    if (open) {
      const t = slot === "Supplements" ? "supplement" : "meal";
      setType(t); setRefId(""); setAmount(""); setUnit("oz");
    }
  }, [open, slot]);

  const list = type === "meal" ? meals : type === "supplement" ? supplements : foods;

  const pick = (id) => {
    setRefId(id);
    if (type === "food") { const f = foods.find((x) => x.id === id); if (f) { setAmount(f.serving_amount || ""); setUnit(f.serving_unit || "oz"); } }
  };

  const save = async () => {
    if (!refId) { toast.error("Select an item"); return; }
    const body = { slot, type, ref_id: refId,
      amount: type === "food" && amount ? parseFloat(amount) : null,
      unit: type === "food" ? unit : null };
    try {
      await api.post(`/day/${date}/entries`, body);
      toast.success("Added to " + slot);
      onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Could not add"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-entry-dialog">
        <DialogHeader><DialogTitle className="font-display">Add to {slot}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            {[["meal", "Meal", Utensils], ["food", "Food", Apple], ["supplement", "Supplement", Pill]].map(([t, lbl, Icon]) => (
              <button key={t} onClick={() => { setType(t); setRefId(""); }} data-testid={`entry-type-${t}`}
                className={`flex flex-1 flex-col items-center gap-1 rounded-xl border py-2.5 text-xs font-semibold transition-colors ${
                  type === t ? "border-brand bg-brand-light text-brand" : "border-slate-200 text-slate-500"}`}>
                <Icon className="h-4 w-4" />{lbl}
              </button>
            ))}
          </div>
          <div>
            <Label>Select {type}</Label>
            <Select value={refId} onValueChange={pick}>
              <SelectTrigger className="mt-1.5" data-testid="entry-item-select"><SelectValue placeholder={`Choose a ${type}`} /></SelectTrigger>
              <SelectContent>{list.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {type === "food" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" /></div>
              <div><Label>Unit</Label>
                <Select value={unit} onValueChange={setUnit}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="entry-save-button" className="w-full rounded-full bg-brand hover:bg-brand-hover">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjRow({ label, food, supp }) {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  return (
    <div className="grid grid-cols-4 items-center gap-2 py-2 text-sm">
      <span className="font-semibold text-slate-500">{label}</span>
      <span className="text-right font-mono">{money(food, cur)}</span>
      <span className="text-right font-mono">{money(supp, cur)}</span>
      <span className="text-right font-mono font-bold text-brand">{money(food + supp, cur)}</span>
    </div>
  );
}

export default function Today() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [date, setDate] = useState(todayStr());
  const [day, setDay] = useState(null);
  const [foods, setFoods] = useState([]);
  const [meals, setMeals] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [addSlot, setAddSlot] = useState(null);

  const loadDay = useCallback(async () => {
    const { data } = await api.get(`/day/${date}`);
    setDay(data);
  }, [date]);
  useAutoSync(loadDay, [loadDay]);
  useEffect(() => {
    Promise.all([api.get("/foods"), api.get("/meals"), api.get("/supplements")])
      .then(([f, m, s]) => { setFoods(f.data); setMeals(m.data); setSupplements(s.data); });
  }, []);

  const removeEntry = async (id) => { await api.delete(`/day/${date}/entries/${id}`); loadDay(); };

  const bySlot = (slot) => (day?.entries || []).filter((e) => e.slot === slot);
  const fp = day?.food_projection || { daily: 0, weekly: 0, monthly: 0, annual: 0 };
  const sp = day?.supplement_projection || { daily: 0, weekly: 0, monthly: 0, annual: 0 };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Daily Log</h1>
          <p className="text-sm text-slate-500">Add meals, foods & supplements to your day</p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1">
          <button onClick={() => setDate(shift(date, -1))} data-testid="day-prev" className="rounded-full p-1.5 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[120px] text-center text-sm font-semibold" data-testid="day-label">{date === todayStr() ? "Today" : pretty(date)}</span>
          <button onClick={() => setDate(shift(date, 1))} data-testid="day-next" className="rounded-full p-1.5 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Food</p>
          <p className="font-mono text-xl font-extrabold" data-testid="today-food-total">{money(day?.food_total || 0, cur)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Supplements</p>
          <p className="font-mono text-xl font-extrabold" data-testid="today-supp-total">{money(day?.supplement_total || 0, cur)}</p>
        </div>
        <div className="rounded-2xl border-0 bg-brand p-4 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/80">Total</p>
          <p className="font-mono text-xl font-extrabold" data-testid="today-total">{money(day?.total || 0, cur)}</p>
        </div>
      </div>

      {/* Slots */}
      <div className="mt-5 space-y-3">
        {MEAL_SLOTS.map((slot) => {
          const entries = bySlot(slot);
          return (
            <div key={slot} data-testid={`slot-${slot}`} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold">{slot}</h3>
                <button onClick={() => setAddSlot(slot)} data-testid={`add-to-${slot}`}
                  className="flex items-center gap-1 rounded-full bg-brand-light px-3 py-1 text-xs font-semibold text-brand hover:bg-brand/15"><Plus className="h-3.5 w-3.5" />Add</button>
              </div>
              {entries.length === 0 ? (
                <p className="mt-2 text-sm text-slate-300">Nothing yet</p>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {entries.map((e) => (
                    <div key={e.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{e.name}{e.amount ? <span className="text-slate-400"> · {e.amount} {e.unit}</span> : null}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-slate-600">{e.cost != null ? money(e.cost, cur) : "—"}</span>
                        <button onClick={() => removeEntry(e.id)} data-testid={`remove-entry-${e.id}`}
                          className="text-slate-300 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Projections / Total Nutrition Investment */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="projections-card">
        <h3 className="font-display text-lg font-bold">Total Nutrition Investment</h3>
        <p className="text-sm text-slate-500">Projected from this day's cost</p>
        <div className="mt-3 grid grid-cols-4 gap-2 border-b border-slate-100 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span></span><span className="text-right">Food</span><span className="text-right">Supp.</span><span className="text-right">Total</span>
        </div>
        <ProjRow label="Daily" food={fp.daily} supp={sp.daily} />
        <ProjRow label="7-day" food={fp.weekly} supp={sp.weekly} />
        <ProjRow label="30-day" food={fp.monthly} supp={sp.monthly} />
        <ProjRow label="Annual" food={fp.annual} supp={sp.annual} />
      </div>

      <AddEntryDialog open={!!addSlot} onOpenChange={(v) => !v && setAddSlot(null)} slot={addSlot || ""}
        date={date} foods={foods} meals={meals} supplements={supplements} onSaved={loadDay} />
    </div>
  );
}
